import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { useEvidenceConfig } from './useEvidenceConfig';
import { devLog } from '../utils/devLogger';

export interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  category: 'preparation' | 'documentation' | 'communication';
  required?: boolean;
  auto_check?: string; // Type of automatic check (e.g., 'has_completed_prep', 'evidence_coverage')
  threshold?: number; // For percentage-based checks
}

export const useReviewChecklist = (focusId: string, focusType: string = 'internship') => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { config: evidenceConfig } = useEvidenceConfig(focusId);

  useEffect(() => {
    const loadChecklist = async () => {
      try {
        // First check if there's a custom checklist in the focus config
        if (evidenceConfig.reviewChecklist) {
          setChecklist(evidenceConfig.reviewChecklist);
          setLoading(false);
          return;
        }

        // Otherwise load the default template
        const { data: template } = await supabase
          .from('review_checklist_templates')
          .select('items')
          .eq('focus_type', focusType)
          .eq('is_default', true)
          .single();

        if (template?.items) {
          setChecklist(template.items);
        } else {
          // Fallback to hardcoded default
          setChecklist([
            {
              id: 'complete-prep',
              label: 'Complete Review Preparation',
              description: 'Fill out reflections and talking points in the Preparation Wizard',
              category: 'preparation',
              required: true,
              auto_check: 'has_completed_prep'
            },
            {
              id: 'evidence-coverage',
              label: 'Achieve 80%+ Evidence Coverage',
              description: 'Ensure you have strong evidence for most evaluation criteria',
              category: 'documentation',
              required: true,
              auto_check: 'evidence_coverage',
              threshold: 80
            },
            {
              id: 'review-cards',
              label: 'Review Your Career Cards',
              description: 'Refresh your memory on key accomplishments and examples',
              category: 'documentation',
              required: false
            },
            {
              id: 'practice-talking',
              label: 'Practice Talking Points',
              description: 'Rehearse explaining your achievements and impact',
              category: 'preparation',
              required: false
            },
            {
              id: 'schedule-review',
              label: 'Confirm Review Meeting Time',
              description: 'Ensure you have the review meeting scheduled with your manager',
              category: 'communication',
              required: true
            },
            {
              id: 'prepare-questions',
              label: 'Prepare Questions for Manager',
              description: 'Think about feedback you want and questions about future growth',
              category: 'communication',
              required: false
            }
          ]);
        }
      } catch (error) {
        devLog.error('Error loading checklist:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChecklist();
  }, [focusId, focusType, evidenceConfig.reviewChecklist]);

  return { checklist, loading };
};

/**
 * Hook to save custom checklist for a focus
 */
export const useSaveReviewChecklist = () => {
  const saveChecklist = async (focusId: string, checklist: ChecklistItem[]) => {
    try {
      // Update the focus_evidence_config with custom checklist
      const { error } = await supabase
        .from('focus_evidence_config')
        .update({
          review_checklist: checklist,
          updated_at: new Date().toISOString()
        })
        .eq('focus_id', focusId);

      if (error) throw error;
      return true;
    } catch (error) {
      devLog.error('Error saving checklist:', error);
      return false;
    }
  };

  return { saveChecklist };
};